import type { Ref } from "react";

import type { DiagnosticOption } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ChipsProps {
  options: DiagnosticOption[];
  /** Indices of the selected options. */
  selected: number[];
  /** True once the answer has been sent: chips lock and stop responding. */
  locked: boolean;
  onToggle: (index: number) => void;
  /**
   * Attached to the first chip so the interview can move keyboard focus onto a
   * newly asked question instead of dropping it on `document.body`.
   */
  firstChipRef?: Ref<HTMLButtonElement>;
}

/** The answer chips under a question. */
export function Chips({ options, selected, locked, onToggle, firstChipRef }: ChipsProps) {
  return (
    <div role="group" aria-label="Answer options" className="mt-4 flex flex-wrap gap-1.5">
      {options.map((option, index) => {
        const isSelected = selected.includes(index);
        return (
          <button
            key={option.label}
            ref={index === 0 ? firstChipRef : undefined}
            type="button"
            aria-pressed={isSelected}
            disabled={locked}
            onClick={() => onToggle(index)}
            className={cn(
              "focus-visible:outline-hw-blue cursor-pointer rounded-[4px] border px-3 py-2 text-left text-[0.875rem] leading-[1.3] font-normal transition-colors focus-visible:outline-2 focus-visible:outline-offset-[3px]",
              isSelected
                ? "border-hw-blue bg-hw-blue text-hw-paper"
                : "border-hw-line bg-hw-card text-hw-ink hover:border-hw-ink",
              locked && "cursor-default",
              locked && !isSelected && "opacity-45",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
